// Страховка от застрявшей сборки (правка 2026-07-31).
//
// Что случилось. Service worker отдавал навигацию из своего прекэша, поэтому
// браузер не видел новый index.html: старая оболочка тянула старые чанки (деплой
// их не удаляет — есть retention), и владелец больше суток работал на бандле
// недельной давности, хотя жал ⌘+Shift+R. Хард-релоад тут и не должен помогать:
// он обходит HTTP-кэш, но не service worker.
//
// Корень вылечен в vite.config (navigateFallback убран), но одного этого мало:
// клиент со СТАРЫМ SW обновится только когда его SW сам переустановится. Этот
// сторож — второй рубеж, не зависящий от поведения SW: сверяем имя главного
// чанка в загруженной странице с тем, что сервер отдаёт прямо сейчас, и при
// расхождении сносим SW с кэшами и перезагружаемся.
//
// Стоит один запрос за загрузку приложения (index.html и так отдаётся no-store).

/**
 * Защита от цикла: одна вынужденная перезагрузка НА КАЖДУЮ серверную сборку.
 *
 * Раньше флаг был просто «уже пробовали» и ставился на вкладку навсегда: одна
 * попытка — и больше никогда, пока вкладку не закроют. Владелец школы держит
 * CRM открытой сутками, поэтому у него плашка «вышло обновление» висела и не
 * уходила: сторож своё право уже израсходовал (жалоба 14.08).
 *
 * Теперь храним ИМЯ серверного чанка, на котором пробовали. Тот же чанк —
 * молчим (цикла не будет). Новый деплой — новое имя, значит вкладка получает
 * ещё одну попытку и вылезает из застревания сама.
 */
const RELOAD_FLAG = 'stale-guard-reloaded';

/** Имя главного чанка в разметке: /assets/index-XXXX.js */
const MAIN_CHUNK_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

/** Чанк, С КОТОРЫМ загрузилась эта вкладка. */
export function loadedMainChunk(): string | null {
  const src = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/index-"]',
  )?.src;
  return src ? (MAIN_CHUNK_RE.exec(src)?.[0] ?? null) : null;
}

/**
 * Чанк, который сервер отдаёт ПРЯМО СЕЙЧАС. `null` — не удалось узнать (нет
 * сети, ошибка): это не повод считать сборку устаревшей.
 */
export async function serverMainChunk(): Promise<string | null> {
  try {
    const res = await fetch('/index.html', { cache: 'no-store' });
    if (!res.ok) return null;
    return MAIN_CHUNK_RE.exec(await res.text())?.[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * Разошлась ли сборка вкладки с серверной. Общая для двух рубежей: сторожа на
 * старте (перезагружает сам) и плашки «вышло обновление» (зовёт человека).
 */
export function isStale(loaded: string | null, server: string | null): boolean {
  if (!loaded || !server) return false;
  return !loaded.endsWith(server);
}

export async function ensureFreshBuild(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (import.meta.env.MODE !== 'production') return;

  const loaded = loadedMainChunk();
  if (!loaded) return;

  // Нет сети или сервер не ответил — не наше дело: приложение работает с тем,
  // что загрузилось.
  const fresh = await serverMainChunk();
  if (!isStale(loaded, fresh)) return;

  // Сборка на сервере другая. Одна попытка на КАЖДУЮ серверную версию: если
  // после перезагрузки расхождение осталось (кэш прокси, застрявший SW),
  // крутить бесконечно нельзя — но и молчать до конца жизни вкладки тоже
  // нельзя, иначе следующий деплой её уже не вытащит.
  try {
    if (sessionStorage.getItem(RELOAD_FLAG) === fresh) return;
    sessionStorage.setItem(RELOAD_FLAG, fresh ?? '1');
  } catch {
    // Приватный режим — обойдёмся без защиты от цикла, но перезагрузим.
  }

  await dropServiceWorkerAndCaches();
  window.location.reload();
}

/**
 * Перезагрузиться НА СВЕЖУЮ сборку — по явному действию человека.
 *
 * Кнопка «Обновить» в плашке раньше звала просто `location.reload()`, и это не
 * работало: страницу обслуживает тот же service worker, он отдаёт ту же старую
 * оболочку, вкладка возвращается на прежнем бандле — и плашка появляется снова
 * (жалоба владельца 13.08, кнопка «не срабатывает»). Хард-релоад тут тоже не
 * помогает: он обходит HTTP-кэш, но не SW.
 *
 * Поэтому делаем ровно то же, что сторож на старте: снимаем регистрацию,
 * чистим кэши и только потом перезагружаемся.
 *
 * ДВЕ СТРАХОВКИ (правка 04.09.2026, жалоба владельца: плашка «Обновляем…»
 * висела у сотрудника со вчерашнего вечера).
 *
 *  1. Снятие SW и чистка кэшей — под таймаутом. На телефоне эти промисы
 *     иногда не выполняются и не отклоняются вовсе; без таймаута до
 *     перезагрузки дело не доходило никогда, а кнопка оставалась в «Обновляем…».
 *  2. Если через две секунды вкладка всё ещё жива, перезагрузка не случилась
 *     (замороженная фоновая вкладка, отложенный reload) — уходим по адресу с
 *     одноразовым параметром: он гарантированно даёт новую навигацию.
 */
const DROP_TIMEOUT_MS = 3000;
const RELOAD_FALLBACK_MS = 2000;

function withTimeout(p: Promise<void>, ms: number): Promise<void> {
  return Promise.race([p, new Promise<void>((resolve) => setTimeout(resolve, ms))]);
}

export async function reloadToFreshBuild(): Promise<void> {
  await withTimeout(dropServiceWorkerAndCaches(), DROP_TIMEOUT_MS);
  window.location.reload();
  setTimeout(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString(36));
    window.location.replace(url.toString());
  }, RELOAD_FALLBACK_MS);
}

/**
 * Снимаем регистрацию SW и чистим его кэши.
 *
 * Без этого перезагрузка ничего не даст: страницу по-прежнему обслуживал бы тот
 * же SW и отдал ту же старую оболочку.
 */
async function dropServiceWorkerAndCaches(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* браузер не дал — пробуем хотя бы кэши */
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* не критично: главное — снятая регистрация */
  }
}
